import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  mockBillError,
  mockBillFullyPaid,
  mockBillSuccess,
} from './data';

function isMockEnabled(): boolean {
  return process.env.VUE_APP_USE_MOCK === 'true';
}

function mockResponse<T>(
  config: AxiosRequestConfig,
  data: T,
  status = 200,
): Promise<AxiosResponse<T>> {
  return Promise.resolve({
    data,
    status,
    statusText: status === 200 ? 'OK' : 'Error',
    headers: { 'x-mock': 'true' },
    config,
    request: {},
  });
}

function resolveMock(config: AxiosRequestConfig): Promise<AxiosResponse> | null {
  const url = `${config.baseURL || ''}${config.url || ''}`;

  const billMatch = url.match(/furijat\/billinfo\/([^/?]+)/i);
  if (billMatch) {
    const invoiceNumber = decodeURIComponent(billMatch[1]);

    // 999... → error scenario for UI testing
    if (/^9+$/.test(invoiceNumber)) {
      return mockResponse(config, mockBillError);
    }

    // 222... → fully paid
    if (/^2+$/.test(invoiceNumber)) {
      return mockResponse(config, mockBillFullyPaid(invoiceNumber));
    }

    return mockResponse(config, mockBillSuccess(invoiceNumber));
  }

  return null;
}

/**
 * Short-circuits API calls with local mock payloads when VUE_APP_USE_MOCK=true.
 * No VPN / BE / Apigee required.
 */
export function setupMocks(axiosInstance: AxiosInstance): void {
  if (!isMockEnabled()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.info('[Furijat] Mock API mode enabled — using local fake data');

  axiosInstance.interceptors.request.use((config) => {
    const mock = resolveMock(config);
    if (mock) {
      // eslint-disable-next-line no-param-reassign
      config.adapter = () => mock;
    }
    return config;
  });
}

export { isMockEnabled };
